type MetricFields = Record<
  string,
  string | number | boolean | null | undefined
>

export type ImportSessionClientMetricName =
  | 'session_revalidate'
  | 'identity_shell_revalidate'
  | 'flow_snapshot_revalidate'
  | 'flow_snapshot_local_update'
  | 'preview_projection_revalidate'
  | 'preview_projection_local_update'
  | 'preview_projection_revalidate_skipped'
  | 'status_projection_revalidate'
  | 'status_projection_revalidate_skipped'
  | 'workbook_projection_revalidate'
  | 'workbook_projection_local_update'
  | 'workbook_projection_revalidate_skipped'
  | 'context_materials_projection_revalidate'
  | 'context_materials_projection_local_update'
  | 'context_materials_projection_revalidate_skipped'
  | 'route_transition_completed'
  | 'audience_handoff_completed'
  | 'websocket_connection_state'
  | 'websocket_event_received'
  | 'websocket_event_ignored'
  | 'websocket_resync'
  | 'visibility_resync'
  | 'client_metric_flush'
  | 'preview_click_to_create_api'

type ImportSessionClientMetricEvent = {
  id: string
  name: ImportSessionClientMetricName
  at: string
  fields: Record<string, string | number | boolean | null>
}

type ImportSessionClientMetricsStore = {
  generatedAt: string
  counters: Partial<Record<ImportSessionClientMetricName, number>>
  recentEvents: ImportSessionClientMetricEvent[]
  pendingEvents: ImportSessionClientMetricEvent[]
  nextId: number
}

declare global {
  interface Window {
    __audienceImportClientMetrics?: ImportSessionClientMetricsStore
  }
}

function getOrCreateStore(): ImportSessionClientMetricsStore | null {
  if (typeof window === 'undefined') {
    return null
  }
  const existing = window.__audienceImportClientMetrics
  if (existing) {
    return existing
  }
  const created: ImportSessionClientMetricsStore = {
    generatedAt: new Date().toISOString(),
    counters: {},
    recentEvents: [],
    pendingEvents: [],
    nextId: 1,
  }
  window.__audienceImportClientMetrics = created
  return created
}

export function incrementImportSessionClientMetric(
  name: ImportSessionClientMetricName,
  fields: MetricFields = {},
) {
  const store = getOrCreateStore()
  if (!store) {
    return
  }

  const at = new Date().toISOString()
  const id = `client-metric-${store.nextId}`
  store.nextId += 1
  const sanitizedFields = Object.fromEntries(
    Object.entries(fields)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, value ?? null]),
  ) as Record<string, string | number | boolean | null>

  store.generatedAt = at
  store.counters[name] = (store.counters[name] ?? 0) + 1
  const event = {
    id,
    name,
    at,
    fields: sanitizedFields,
  }
  store.recentEvents = [event, ...store.recentEvents].slice(0, 50)
  store.pendingEvents = [...store.pendingEvents, event].slice(-50)
}

export function getImportSessionClientMetrics() {
  return typeof window === 'undefined'
    ? null
    : (window.__audienceImportClientMetrics ?? null)
}

export function resetImportSessionClientMetrics() {
  if (typeof window !== 'undefined') {
    delete window.__audienceImportClientMetrics
  }
}

export function getPendingImportSessionClientMetrics(sessionId: string) {
  const store = getOrCreateStore()
  if (!store) {
    return []
  }
  return store.pendingEvents.filter(
    (event) => event.fields.sessionId === sessionId,
  )
}

export function markImportSessionClientMetricsFlushed(eventIds: string[]) {
  if (!eventIds.length) {
    return
  }
  const store = getOrCreateStore()
  if (!store) {
    return
  }
  const flushed = new Set(eventIds)
  store.pendingEvents = store.pendingEvents.filter((event) => !flushed.has(event.id))
}

/**
 * Flushes pending client metrics to the backend.
 *
 * Uses raw fetch with keepalive: true instead of the shared API client (axios).
 * This is a deliberate exception: keepalive ensures the request completes even
 * when the page is unloading (pagehide/visibilitychange). Axios does not
 * support the fetch keepalive option, so using the shared client would lose
 * reliability for tab-close and navigation-away flush paths.
 */
export async function flushImportSessionClientMetrics(
  sessionId: string,
  requestId = `import-session-client-metrics-${sessionId}`,
) {
  if (typeof window === 'undefined') {
    return { accepted: 0 }
  }
  const pendingEvents = getPendingImportSessionClientMetrics(sessionId)
  if (!pendingEvents.length) {
    return { accepted: 0 }
  }

  const response = await window.fetch(
    `/api/populations/import-sessions/${sessionId}/client-metrics`,
    {
      method: 'POST',
      keepalive: true,
      credentials: 'include' as RequestCredentials,
      headers: {
        'Content-Type': 'application/json',
        'x-request-id': requestId,
      },
      body: JSON.stringify({
        events: pendingEvents,
      }),
    },
  )

  if (!response.ok) {
    throw new Error(`Failed to flush client metrics: ${response.status}`)
  }

  markImportSessionClientMetricsFlushed(pendingEvents.map((event) => event.id))
  return {
    accepted: pendingEvents.length,
  }
}

/* Navigation metrics (route transitions & audience handoffs) */

type MetricMarker = {
  sessionId: string
  startedAt: number
  sourceRoute: string | null
  targetRoute: string | null
  trigger: string
  audienceId?: string | null
}

const ROUTE_TRANSITION_MARKERS_KEY = 'audience-import-route-transition-markers'
const AUDIENCE_HANDOFF_MARKERS_KEY = 'audience-import-audience-handoff-markers'

function readMarkers(storageKey: string): MetricMarker[] {
  if (typeof window === 'undefined') {
    return []
  }
  try {
    const raw = window.sessionStorage.getItem(storageKey)
    if (!raw) {
      return []
    }
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed)
      ? parsed.filter((marker) => {
          if (!marker || typeof marker !== 'object') {
            return false
          }
          return (
            typeof marker.sessionId === 'string' &&
            typeof marker.startedAt === 'number' &&
            typeof marker.trigger === 'string'
          )
        })
      : []
  } catch {
    return []
  }
}

function writeMarkers(storageKey: string, markers: MetricMarker[]) {
  if (typeof window === 'undefined') {
    return
  }
  if (markers.length === 0) {
    window.sessionStorage.removeItem(storageKey)
    return
  }
  window.sessionStorage.setItem(storageKey, JSON.stringify(markers.slice(-10)))
}

function upsertMarker(storageKey: string, marker: MetricMarker) {
  const markers = readMarkers(storageKey).filter(
    (candidate) =>
      !(
        candidate.sessionId === marker.sessionId &&
        candidate.trigger === marker.trigger &&
        candidate.targetRoute === marker.targetRoute
      ),
  )
  markers.push(marker)
  writeMarkers(storageKey, markers)
}

function completeMarker(
  storageKey: string,
  sessionId: string,
  predicate: (marker: MetricMarker) => boolean,
) {
  const now = Date.now()
  const markers = readMarkers(storageKey)
  const completed = markers.find(
    (marker) => marker.sessionId === sessionId && predicate(marker),
  )
  if (!completed) {
    return null
  }
  writeMarkers(
    storageKey,
    markers.filter((marker) => marker !== completed),
  )
  return {
    ...completed,
    durationMs: Math.max(0, now - completed.startedAt),
  }
}

export function startImportSessionRouteTransition(args: {
  sessionId: string
  sourceRoute?: string | null
  targetRoute: string
  trigger: string
}) {
  if (!args.sessionId || !args.targetRoute) {
    return
  }
  upsertMarker(ROUTE_TRANSITION_MARKERS_KEY, {
    sessionId: args.sessionId,
    startedAt: Date.now(),
    sourceRoute: args.sourceRoute?.trim() || null,
    targetRoute: args.targetRoute.trim(),
    trigger: args.trigger,
  })
}

export function completeImportSessionRouteTransition(args: {
  sessionId: string
  currentRoute: string | null | undefined
}) {
  const currentRoute = args.currentRoute?.trim() || null
  if (!args.sessionId || !currentRoute) {
    return
  }
  const completed = completeMarker(
    ROUTE_TRANSITION_MARKERS_KEY,
    args.sessionId,
    (marker) => marker.targetRoute === currentRoute,
  )
  if (!completed) {
    return
  }
  incrementImportSessionClientMetric('route_transition_completed', {
    sessionId: args.sessionId,
    sourceRoute: completed.sourceRoute,
    targetRoute: completed.targetRoute,
    trigger: completed.trigger,
    durationMs: completed.durationMs,
  })
  void flushImportSessionClientMetrics(args.sessionId).catch(() => undefined)
}

export function startAudienceHandoff(args: {
  sessionId: string
  sourceRoute?: string | null
  trigger: string
}) {
  if (!args.sessionId) {
    return
  }
  upsertMarker(AUDIENCE_HANDOFF_MARKERS_KEY, {
    sessionId: args.sessionId,
    startedAt: Date.now(),
    sourceRoute: args.sourceRoute?.trim() || null,
    targetRoute: null,
    trigger: args.trigger,
    audienceId: null,
  })
}

export function completeAudienceHandoff(args: {
  sessionId: string
  audienceId: string
  targetRoute: string
}) {
  if (!args.sessionId || !args.audienceId || !args.targetRoute) {
    return
  }
  const completed = completeMarker(
    AUDIENCE_HANDOFF_MARKERS_KEY,
    args.sessionId,
    () => true,
  )
  if (!completed) {
    return
  }
  incrementImportSessionClientMetric('audience_handoff_completed', {
    sessionId: args.sessionId,
    sourceRoute: completed.sourceRoute,
    targetRoute: args.targetRoute,
    trigger: completed.trigger,
    audienceId: args.audienceId,
    durationMs: completed.durationMs,
  })
  void flushImportSessionClientMetrics(args.sessionId).catch(() => undefined)
}
